import { IPollable } from "@shared/services/PollingService/IPollable";
import { throttle } from "lodash-es";

import { localStorageService } from "../LocalStorageService/LocalStorageService";
import { poller } from "../PollingService/PollingService";

const DEFAULT_IDLE_TIMEOUT = 44_000;
const WATCH_POLLING_INTERVAL = 2_000;
const STORAGE_KEY = "_expiredTime";

class UserWatchActivityService {
  private readonly timeout: number;
  private readonly poller: IPollable;
  private _isUserActive = true;
  private _disposeFetchersCbs = new Set<{ disposeCallback: () => void }>();

  constructor() {
    this.timeout = DEFAULT_IDLE_TIMEOUT;
    this.poller = poller;
  }

  public init() {
    if (!this.poller) {
      throw Error("Poller not initialized");
    }

    this.handleActivityTracker();
    this.setIdleTimer();

    const updateActivityFetcher = this.poller.poll(
      this.updateExpiredTime,
      WATCH_POLLING_INTERVAL,
      `[user idle timeout] ${WATCH_POLLING_INTERVAL / 1000} s`
    );

    this._disposeFetchersCbs.add(updateActivityFetcher);
  }

  public dispose() {
    this.handleCleanUpTracker();
    this._disposeFetchersCbs.forEach(({ disposeCallback }) => {
      disposeCallback();
    });
  }

  public get isUserActive(): boolean {
    return this._isUserActive;
  }

  private set isUserActive(value: boolean) {
    this._isUserActive = value;
  }

  private updateExpiredTime = async (): Promise<void> => {
    const expiredTime = parseInt(
      localStorageService.getData(STORAGE_KEY) || "0",
      10
    );

    if (expiredTime && expiredTime < Date.now()) {
      this.isUserActive = false;
    }
  };

  private handleActivityTracker() {
    window.addEventListener("mousemove", this.setIdleTimer);
    window.addEventListener("scroll", this.setIdleTimer);
    window.addEventListener("keydown", this.setIdleTimer);
    window.addEventListener("touchmove", this.setIdleTimer);
  }

  private handleCleanUpTracker() {
    localStorageService.removeData("_expiredTime");
    window.removeEventListener("mousemove", this.setIdleTimer);
    window.removeEventListener("scroll", this.setIdleTimer);
    window.removeEventListener("keydown", this.setIdleTimer);
    window.removeEventListener("touchmove", this.setIdleTimer);
  }

  private setIdleTimer = throttle(() => {
    this.isUserActive = true;
    localStorageService.saveData(
      STORAGE_KEY,
      String(Date.now() + this.timeout)
    );
  }, 300);
}

export const userWatchActivityService = new UserWatchActivityService();
